/*
 * @Author: Ryan
 * @Date: 2026-01-13
 * @Description: AI TikTok Video Generator Landing 页面
 */
import { GetStaticProps } from 'next';
import React from 'react';

import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead from '@/landing/components/SEOHead';
import FAQSection, { FAQItem } from '@/landing/components/templates/FAQSection';
import FeaturesSection, {
  FeatureItem,
} from '@/landing/components/templates/FeaturesSection';
import FooterCTASection from '@/landing/components/templates/FooterCTASection';
import HowToSection, { StepData } from '@/landing/components/templates/HowToSection';
import TikTokExploreMoreSection from '@/landing/components/templates/TikTokExploreMoreSection';
import TikTokHeroNew from '@/landing/components/templates/TikTokHeroNew';
import TikTokHeroSupportingSection, {
  ToolItem,
} from '@/landing/components/templates/TikTokHeroSupportingSection';
import TikTokUseCasesSection, {
  UseCaseItem,
} from '@/landing/components/templates/TikTokUseCasesSection';
import WhyChooseSection, {
  ReasonItem,
} from '@/landing/components/templates/WhyChooseSection';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 加载 JSON 内容
const loadContent = async (): Promise<Record<string, string>> => {
  try {
    const content = await import('@/landing/tiktok-video-generator/data/content.json');
    return content.default || content;
  } catch (error) {
    console.error('Failed to load content', error);
    return {};
  }
};

interface TikTokVideoGeneratorPageProps {
  content: Record<string, string>;
}

const TikTokVideoGeneratorPage: React.FC<TikTokVideoGeneratorPageProps> = ({
  content,
}) => {
  // SEO 信息
  const pageTitle =
    content['metaData.metaTitle'] || 'AI TikTok Video Generator | FalcoCut';
  const metaDescription = content['metaData.metaDescription'] || '';
  const currentUrl = 'https://falcocut.ai/ai-tiktok-video-generator';

  // 结构化数据
  const structuredData = createSoftwareAppSchema({
    name: 'FalcoCut AI TikTok Video Generator',
    description: metaDescription,
    url: currentUrl,
    ratingValue: '4.8',
    ratingCount: '1250',
  });

  // Hero Section
  const heroTitle = content['hero.h1'] || '';
  const heroSubtitle = content['hero.subheading'] || '';
  const heroCta = content['hero.cta'] || 'Create TikTok Videos with AI';
  // 读取视频列表和封面
  const heroVideos: string[] = [];
  const heroPosters: string[] = [];
  for (let i = 1; i <= 3; i++) {
    const videoUrl = content[`hero.video${i}`];
    const posterUrl = content[`hero.poster${i}`];
    if (videoUrl) {
      heroVideos.push(videoUrl);
    }
    if (posterUrl) {
      heroPosters.push(posterUrl);
    }
  }

  // Hero Supporting Section
  const heroSupportingTitle = content['heroSupporting.h2'] || '';
  const heroSupportingSubtitle = content['heroSupporting.subheading'] || '';
  const heroSupportingTools: ToolItem[] = [];
  for (let i = 1; i <= 3; i++) {
    const toolTitle = content[`heroSupporting.tool${i}_title`];
    const toolText = content[`heroSupporting.tool${i}_text`];
    if (toolTitle && toolText) {
      heroSupportingTools.push({
        title: toolTitle,
        description: toolText,
      });
    }
  }

  // How To Section
  const howToTitle = content['howTo.h2'] || '';
  const howToSteps: StepData[] = [];
  for (let i = 1; i <= 4; i++) {
    const stepTitle = content[`howTo.step${i}_title`];
    const stepText = content[`howTo.step${i}_text`];
    if (stepTitle && stepText) {
      howToSteps.push({
        number: i,
        title: stepTitle,
        description: stepText,
      });
    }
  }

  // Features Section
  const featuresTitle = content['features.h2'] || '';
  const featuresSubtitle = content['features.subheading'] || '';
  const features: FeatureItem[] = [];
  // 功能图片
  const featureImages = [
    'https://assets.falcocut.ai/tools/tiktok-video-generator/prompts-to-video.webp',
    'https://assets.falcocut.ai/tools/tiktok-video-generator/trend-style-clips.webp',
    'https://assets.falcocut.ai/tools/tiktok-video-generator/product-to-video.webp',
    'https://assets.falcocut.ai/tools/tiktok-video-generator/voiceovers-captions.webp',
    'https://assets.falcocut.ai/tools/tiktok-video-generator/long-to-shorts.webp',
    'https://assets.falcocut.ai/tools/tiktok-video-generator/publish-ready.webp',
  ];
  for (let i = 1; i <= 6; i++) {
    const featureTitle = content[`features.feature${i}_title`];
    const featureText = content[`features.feature${i}_text`];
    if (featureTitle && featureText) {
      features.push({
        title: featureTitle,
        description: featureText,
        imageUrl: featureImages[i - 1],
        imageAlt: featureTitle,
      });
    }
  }

  // Use Cases Section
  const useCasesTitle = content['useCases.h2'] || '';
  const useCasesCta = content['useCases.cta'] || 'Learn more';
  const useCases: UseCaseItem[] = [];
  for (let i = 1; i <= 6; i++) {
    const useCaseTitle = content[`useCases.useCase${i}_title`];
    const useCaseText = content[`useCases.useCase${i}_text`];
    if (useCaseTitle && useCaseText) {
      useCases.push({
        title: useCaseTitle,
        description: useCaseText,
      });
    }
  }

  // Why Choose Section
  const whyChooseTitle = content['whyChoose.h2'] || '';
  const whyChooseSubtitle = content['whyChoose.subheading'] || '';
  const whyChooseReasons: ReasonItem[] = [];
  for (let i = 1; i <= 6; i++) {
    const reasonTitle = content[`whyChoose.reason${i}_title`];
    const reasonText = content[`whyChoose.reason${i}_text`];
    if (reasonTitle && reasonText) {
      whyChooseReasons.push({
        title: reasonTitle,
        description: reasonText,
      });
    }
  }

  // FAQ Section
  const faqTitle = content['faq.h2'] || '';
  const faqs: FAQItem[] = [];
  for (let i = 1; i <= 12; i++) {
    const question = content[`faq.q${i}`];
    const answer = content[`faq.a${i}`];
    if (question && answer) {
      faqs.push({
        question,
        answer,
      });
    }
  }

  // Footer CTA Section
  const footerCtaTitle = content['footerCta.h2'] || '';
  const footerCtaSubtitle = content['footerCta.subheading'] || '';
  const footerCtaCta = content['footerCta.cta'] || 'Create TikTok Videos Now';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={metaDescription}
        url={currentUrl}
        imageAlt="FalcoCut AI TikTok Video Generator"
        publishedTime="2026-01-13"
        modifiedTime="2026-01-16"
        structuredData={structuredData}
      />

      {/* 1. Hero Section */}
      <TikTokHeroNew
        title={heroTitle}
        subtitle={heroSubtitle}
        ctaText={heroCta}
        videos={heroVideos}
        posters={heroPosters}
      />

      {/* 2. Hero Supporting Section */}
      {heroSupportingTools.length > 0 && (
        <TikTokHeroSupportingSection
          title={heroSupportingTitle}
          subtitle={heroSupportingSubtitle}
          tools={heroSupportingTools}
          ctaText={heroCta}
        />
      )}

      {/* 3. How To Section */}
      {howToSteps.length > 0 && (
        <HowToSection
          title={howToTitle}
          steps={howToSteps}
          ctaText={heroCta}
          imageUrl="https://assets.falcocut.ai/tools/tiktok-video-generator/how-to.webp"
        />
      )}

      {/* 4. Features Section */}
      {features.length > 0 && (
        <FeaturesSection
          title={featuresTitle}
          subtitle={featuresSubtitle}
          features={features}
          ctaText="Create TikTok Videos"
        />
      )}

      {/* 5. Use Cases Section */}
      {useCases.length > 0 && (
        <TikTokUseCasesSection
          title={useCasesTitle}
          useCases={useCases}
          ctaText={useCasesCta}
        />
      )}

      {/* 6. Why Choose Section */}
      {whyChooseReasons.length > 0 && (
        <WhyChooseSection
          title={whyChooseTitle}
          subtitle={whyChooseSubtitle}
          reasons={whyChooseReasons}
          ctaText="Create TikTok Videos"
        />
      )}

      {/* 7. Explore More Section */}
      <TikTokExploreMoreSection />

      {/* 8. FAQ Section */}
      {faqs.length > 0 && <FAQSection title={faqTitle} faqs={faqs} />}

      {/* 9. Footer CTA Section */}
      {footerCtaTitle && (
        <FooterCTASection
          title={footerCtaTitle}
          subtitle={footerCtaSubtitle}
          ctaText={footerCtaCta}
        />
      )}
    </>
  );
};

export const getStaticProps: GetStaticProps<TikTokVideoGeneratorPageProps> = async () => {
  const content = await loadContent();

  return {
    props: {
      content,
    },
  };
};

export default withLandingLayout(TikTokVideoGeneratorPage);
